'use client'
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getStoredPropertyCode } from '@/utils/propertyManager';

export default function NoPropertyAvailable() {
  const router = useRouter();

  useEffect(() => {
    // Set page title
    document.title = "No Property Available - Booking Engine";
  }, []);

  const handleGoBack = () => {
    // Get saved property from localStorage
    const savedProperty = getStoredPropertyCode();
    console.log('🔙 Going back with saved property:', savedProperty);
    
    // Try to go back to previous page, or redirect to home with saved/default property
    if (window.history.length > 1) {
      router.back();
    } else {
      // Redirect to home with saved property (or PMH as fallback) and current date
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = new URLSearchParams({
        checkIn: formatDate(today),
        checkOut: formatDate(tomorrow),
        adults: '2',
        children: '0',
        room: '1',
        property: savedProperty || 'PMH'  // Use saved property or PMH as fallback
      });

      console.log('✓ Redirecting to:', `/?${params.toString()}`);
      router.push(`/?${params.toString()}`);
    }
  };

  const handleSelectProperty = (propertyCode) => {
    // Get current URL parameters and preserve them
    const urlParams = new URLSearchParams(window.location.search);
    
    // Set the new property
    urlParams.set('property', propertyCode);
    
    // If no other parameters exist, set defaults
    if (!urlParams.has('checkIn')) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      urlParams.set('checkIn', formatDate(today));
      urlParams.set('checkOut', formatDate(tomorrow));
      urlParams.set('adults', '2');
      urlParams.set('children', '0');
      urlParams.set('room', '1');
    }

    router.push(`/?${urlParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            No Property Available
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            The property you're looking for is not available or doesn't exist.
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Available Properties
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => handleSelectProperty('DEMO')}
                  className="w-full flex justify-between items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <div className="text-left">
                      <div className="font-medium">Demo Hotel</div>
                      <div className="text-xs text-gray-500">Property Code: DEMO</div>
                    </div>
                  </div>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <button
                onClick={handleGoBack}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Go Back
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Need help? Contact our support team for assistance with property selection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
